import React, {useState, useEffect} from 'react';
import {useParams, useNavigate, Link as RouterLink} from 'react-router-dom';
import {
    Box, Typography, TextField, Button, Paper, Stack, Alert,
    Snackbar, CircularProgress, Grid, FormControlLabel, Switch,
    MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import apiService from '../../services/apiService';
import EntityCriteriaEditor from '../../components/EntityCriteriaEditor';
import {ProtectedFieldSet} from '../../context/AuthDisabledContext';
import Protected from '../../components/Protected';
import {ROLES} from '../../config/roles';

const DailyBonusConfigEditPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const requiredAction = isNew ? 'create' : 'update';

    const [dayNumber, setDayNumber] = useState('');
    const [reward, setReward] = useState([]);
    const [enabled, setEnabled] = useState(true);

    const [loading, setLoading] = useState(!isNew);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: ''});

    useEffect(() => {
        if (!isNew) {
            apiService.get(`/api/daily-bonus-configs/${id}`)
                .then(data => {
                    setDayNumber(String(data.dayNumber ?? ''));
                    setReward(data.reward || []);
                    setEnabled(data.enabled ?? true);
                })
                .catch(err => setError(err.message || 'Failed to load daily bonus config'))
                .finally(() => setLoading(false));
        }
    }, [id, isNew]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitted(true);
        setIsSaving(true);
        setError(null);

        const rewardError = EntityCriteriaEditor.validate(reward, true);
        if (rewardError) {
            setError('Reward: ' + rewardError);
            setIsSaving(false);
            return;
        }

        const data = {
            dayNumber: parseInt(dayNumber),
            reward: reward.length > 0 ? reward : null,
            enabled,
        };

        try {
            const apiCall = isNew
                ? apiService.post('/api/daily-bonus-configs', data)
                : apiService.put(`/api/daily-bonus-configs/${id}`, data);
            await apiCall;
            setSnackbar({open: true, message: 'Daily bonus config saved!'});
            setTimeout(() => navigate('/daily-bonus-configs'), 800);
        } catch (err) {
            setError(err.message || 'Failed to save daily bonus config');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{p: 4, textAlign: 'center'}}><CircularProgress/></Box>;
    }

    return (
        <Box sx={{p: 2}} component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" gutterBottom>
                {isNew ? 'Create Daily Bonus Config' : `Edit Day ${id}`}
            </Typography>

            <Protected component={DailyBonusConfigEditPage} action={requiredAction} mode="disable">
                <ProtectedFieldSet>
                    <Grid container spacing={3}>
                        <Grid size={{xs: 12, md: 6}}>
                            <Stack spacing={3}>
                                <Paper sx={{p: 2}}>
                                    <Typography variant="h6" gutterBottom>Configuration</Typography>
                                    <Stack spacing={2}>
                                        {isNew ? (
                                            <TextField
                                                required fullWidth select
                                                label="Day Number"
                                                value={dayNumber}
                                                onChange={e => setDayNumber(e.target.value)}
                                            >
                                                {[1, 2, 3, 4, 5, 6, 7].map(d => (
                                                    <MenuItem key={d} value={String(d)}>Day {d}</MenuItem>
                                                ))}
                                            </TextField>
                                        ) : (
                                            <TextField
                                                fullWidth disabled
                                                label="Day Number"
                                                value={`Day ${dayNumber}`}
                                            />
                                        )}
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={enabled}
                                                    onChange={e => setEnabled(e.target.checked)}
                                                />
                                            }
                                            label="Enabled"
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Grid>

                        <Grid size={{xs: 12, md: 6}}>
                            <Paper sx={{p: 2}}>
                                <Typography variant="h6" gutterBottom>Reward</Typography>
                                <EntityCriteriaEditor
                                    value={reward}
                                    onChange={setReward}
                                    preset="reward"
                                    showErrors={submitted}
                                    allowRange
                                />
                            </Paper>
                        </Grid>
                    </Grid>

                    {error && <Alert severity="error" sx={{mt: 3}}>{error}</Alert>}

                    <Stack direction="row" spacing={2} sx={{mt: 3}}>
                        <Protected component={DailyBonusConfigEditPage} action={requiredAction}>
                            <Button
                                type="submit" variant="contained"
                                startIcon={isSaving ? <CircularProgress size={20}/> : <SaveIcon/>}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </Button>
                        </Protected>
                        <Button component={RouterLink} to="/daily-bonus-configs"
                                variant="outlined" startIcon={<ArrowBackIcon/>}>
                            Back to List
                        </Button>
                    </Stack>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({open: false, message: ''})}
                        message={snackbar.message}
                    />
                </ProtectedFieldSet>
            </Protected>
        </Box>
    );
};

DailyBonusConfigEditPage.abilities = {
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
};

export default DailyBonusConfigEditPage;
